import { format, isSameDay } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useContext } from 'react'
import { Spacing } from '@opallabs/gem'
import {
  AllowedEvents,
  AnalyticsConsumer,
  AnalyticsLayer,
  TrackEvent,
} from '@opallabs/quanta'
import CardContext from '../../cardContext'
import { ContentCard } from '../../ContentCard'
import { useContentDragContext } from '../../ContentDragContext'
import { ContentQuickCreate } from '../../ContentQuickCreate'
import { CreateCard } from '../../CreateCard'
import { isWithinStoryDateRange } from '../../helpers'
import { WeekViewCategory, WeekViewSortType } from '../../types'
import { useContentDropProposal } from '../../useContentDropProposal'
import { useContentMap } from '../../useMomentMap'
import { makeMapKey } from '../../useMomentMap/momentMap'
import { WeekViewGlobalContext } from '../../weekViewGlobalContext'
import { useWeekViewSettings } from '../../WeekViewSettingsContext'
import {
  addCardWrapper as momentAddCardWrapper,
  emptyCategoryContainer,
  emptyCategoryPill,
} from '../MomentStack/styles'
import * as styles from './styles'

type ContentStackProps = Readonly<{
  category: WeekViewCategory
  date: Date
  /** Ensures only the designated stack shows the empty-state pill. */
  allowEmptyStatePlaceholder?: boolean
}>

/**
 * Content mode analog of `MomentStack`. Renders `ContentCard`s for each
 * content item in this (category × date) cell. Also:
 *  - Serves as a drop target for content drag-reschedule (prevent-drop if
 *    target day is outside the parent moment's range).
 *  - Publishes the hovered day key to `ContentDragContext` so
 *    `WeekViewDayHeader` can render a column-wide drop overlay that
 *    matches moment-mode's visual treatment.
 *  - Shows a hover-activated "Create" affordance matching moment creation's
 *    visual language (CreateCard shell + CreateMomentCard-style inline
 *    editor). On submit, auto-creates a wrapping moment plus the content.
 */
export const ContentStack: React.FC<ContentStackProps> = ({
  category,
  date,
  allowEmptyStatePlaceholder = false,
}) => {
  const { categoryCounts, contentMap, getContent, getMomentForContent } =
    useContentMap()
  const {
    activeColumn,
    disableActiveColumn,
    isLimitedUser,
    isLoadingMoments,
    isMomentCreatorActive,
    placeholderMoment,
    setDisableActiveColumn,
    setPlaceholderMoment,
    story,
  } = useContext(WeekViewGlobalContext)
  const { draggedContent, setDraggedContent, setHoveredDate } =
    useContentDragContext()
  const { onRescheduleContent } = useContext(CardContext) ?? {}
  const { selectedSort } = useWeekViewSettings()
  const [isCreatorActive, setIsCreatorActive] = React.useState(false)

  const dayKey = format(date, 'yyyy-MM-dd')

  // The CategoryHeader's "+ Create" button sets `placeholderMoment`; we
  // reuse that signal so header- and body-initiated creators stay in sync.
  const isHeaderCreatorActive =
    isMomentCreatorActive &&
    placeholderMoment &&
    isSameDay(new Date(placeholderMoment.scheduled_at), date) &&
    placeholderMoment.day_segment === (category.id ?? null)

  const isCreatorVisible = isCreatorActive || Boolean(isHeaderCreatorActive)

  const key = makeMapKey(date, category.id)
  const contentIds = contentMap.get(key)

  const isActiveColumn =
    isSameDay(date, activeColumn.date) &&
    activeColumn.category === category.title &&
    !disableActiveColumn

  const { forbiddenReason, buildReschedulePayload } =
    useContentDropProposal(date)

  const onDragOver = (e: React.DragEvent) => {
    if (!draggedContent) return
    setHoveredDate(dayKey)
    if (forbiddenReason) return
    // preventDefault on dragover marks this element as a valid drop target
    // for the HTML5 drag-drop API. Without it, `drop` won't fire.
    e.preventDefault()
  }

  // Ignore leaves into descendants; clear only when the cursor exits
  // this stack entirely so the column overlay doesn't get stuck on a
  // stale column when the cursor drifts into dead space.
  const onDragLeave = (e: React.DragEvent) => {
    if (!draggedContent) return
    const next = e.relatedTarget as Node | null
    if (next && e.currentTarget.contains(next)) return
    setHoveredDate(null)
  }

  const onDrop = (e: React.DragEvent, track: TrackEvent) => {
    e.preventDefault()
    const payload = buildReschedulePayload()
    if (!payload) return
    track(AllowedEvents.drop, { id: payload.contentId, type: 'content' })
    onRescheduleContent?.(payload)
    setDraggedContent(null)
    setHoveredDate(null)
  }

  const onClickAdd = () => {
    setDisableActiveColumn(true)
    setIsCreatorActive(true)
  }

  const onExitCreator = () => {
    setIsCreatorActive(false)
    setDisableActiveColumn(false)
    // Clear the header signal so a stale placeholder doesn't linger.
    if (isHeaderCreatorActive) setPlaceholderMoment(null)
  }

  // Disallow creation in the Time sort's "Unscheduled" bucket —
  // quick-create always produces a scheduled moment, so the new content
  // would inherit a time and land in a real hour row instead.
  const isNoTimeBucket =
    selectedSort.type === WeekViewSortType.Time && category.id == null

  // Mirror MomentStack's add-button guard set so restricted users and
  // out-of-story dates can't trigger content creation either.
  let addButtonDisabled = false
  let addButtonTooltip = ''
  if (!isWithinStoryDateRange(date, story)) {
    addButtonDisabled = true
    addButtonTooltip = 'Outside of story date range'
  }
  if (isLimitedUser) {
    addButtonDisabled = true
    addButtonTooltip = 'Current user not permitted to create content'
  }

  const showAddButton =
    isActiveColumn && !isCreatorVisible && !draggedContent && !isNoTimeBucket

  const displayNoContentPlaceholder = React.useMemo(() => {
    const count = categoryCounts.get(category.id)
    return (
      allowEmptyStatePlaceholder && (typeof count !== 'number' || count === 0)
    )
  }, [allowEmptyStatePlaceholder, category.id, categoryCounts])

  return (
    <AnalyticsLayer name="Content Drop Target">
      <AnalyticsConsumer>
        {({ track }) => (
          <div
            css={styles.stack}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={(e) => onDrop(e, track)}
          >
            {contentIds?.map((id, index) => {
              const content = getContent(id)
              if (!content) return null
              const moment = getMomentForContent(id)
              // Mirror `MomentCard/withDraggable.js` — restricted users,
              // in-flight saves, and parent-moment updates can't initiate
              // a drag-reschedule. Unlike the span path, we deliberately do
              // not gate on `Boolean(moment)`: a content card whose parent
              // moment isn't hydrated stays draggable, and the before-moment
              // guard simply no-ops (see `useContentDropProposal`).
              const isDraggable =
                !isLimitedUser &&
                !isLoadingMoments &&
                !content?.isSaving &&
                !moment?.isUpdatingContent
              return (
                <div css={{ marginBlockEnd: Spacing.M }} key={`${key}-${id}`}>
                  <AnalyticsLayer name="Content Card">
                    <ContentCard
                      content={content}
                      moment={moment}
                      position={index}
                      isDraggable={isDraggable}
                    />
                  </AnalyticsLayer>
                </div>
              )
            })}
            {/* Spring-scale intro/exit matches `MomentCard/Default`'s
                treatment of `CreateMomentCard`. */}
            <AnimatePresence>
              {isCreatorVisible && (
                <motion.div
                  key="content-quick-create"
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  initial={{ scale: 0.5, opacity: 0 }}
                  transition={{ type: 'spring', bounce: 0.1, duration: 0.5 }}
                >
                  <AnalyticsLayer name="Content Creator">
                    <ContentQuickCreate
                      date={date}
                      sortType={selectedSort.type}
                      categoryId={category.id ?? null}
                      categoryTitle={category.title}
                      onExitForm={onExitCreator}
                    />
                  </AnalyticsLayer>
                </motion.div>
              )}
            </AnimatePresence>
            {displayNoContentPlaceholder &&
              !isActiveColumn &&
              !isCreatorVisible && (
                <div
                  role="status"
                  aria-live="polite"
                  aria-label={`${category.title} contains no scheduled content`}
                  css={emptyCategoryContainer}
                >
                  <span css={emptyCategoryPill}>No scheduled content</span>
                </div>
              )}
            {/* Wrapper required: CreateCard's outline sizes at 99.9%
                and collapses without a determined-height ancestor. */}
            <div css={momentAddCardWrapper}>
              <CreateCard
                disableButton={addButtonDisabled}
                tooltipMessage={addButtonTooltip}
                isVisible={showAddButton}
                onClickAdd={onClickAdd}
              />
            </div>
          </div>
        )}
      </AnalyticsConsumer>
    </AnalyticsLayer>
  )
}
